import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { PlaceFilter } from './PlaceFilter'
import { Icon } from './Icon'
import { cn } from '@/lib/cn'

/**
 * Choose a place, inside a form.
 *
 * `PlaceFilter` — the same `HierarchyPicker` dialog Settings → Users, the anomaly
 * list, the sensor charts and the top bar use — wearing a form field's clothes: a
 * real label above it, a hint, an error announced in place, and a required
 * marker. The look and the wiring match `Field` so a place sits in a form like
 * every other control in it.
 *
 * This replaced the flattened `ForgeSelect`s that offered the whole estate in
 * one listbox, fed by `useHierarchyNodes`. Those read every site, line and asset
 * in the account before they could open and could not be searched; the picker
 * drills one generation at a time, searched and paged by the server.
 *
 * `allowTiers` limits what may be CHOSEN, not what may be navigated through —
 * a machine-only field still drills via its site and line. The root row (the
 * whole account) is never choosable here; a form that means "everywhere" says so
 * with its own control, as Shift Master does.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {?{id: string, tier: string, name: string, trail?: Array}} props.value
 * @param {(place: ?object) => void} props.onChange
 * @param {Set<string>} [props.allowTiers]  Defaults to site, line and asset.
 * @param {string} [props.placeholder]      What the button reads when empty.
 * @param {string} [props.title]            The dialog's title. Defaults to the label.
 * @param {string} [props.note]             Sentence above the picker's list.
 * @param {string} [props.hint]
 * @param {string} [props.error]
 * @param {boolean} [props.required]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 */
export function PlaceField({
  label,
  value,
  onChange,
  allowTiers = DEFAULT_TIERS,
  placeholder,
  title,
  note,
  hint,
  error,
  required = false,
  disabled = false,
  className,
}) {
  const { t } = useTranslation('app')
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ')

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-control text-ink">
        {label}
        {required ? (
          <span className="ms-1 text-on-red" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      <PlaceFilter
        id={id}
        value={value}
        onChange={onChange}
        allowTiers={allowTiers}
        showLabel={false}
        emptyLabel={placeholder ?? t('placeFilter.choose')}
        title={title ?? label}
        /* Not the filter's default note — "includes everything inside it" is
           true of a filter and false of a record attached to one place. */
        note={note ?? ''}
        disabled={disabled}
        describedBy={describedBy}
        invalid={Boolean(error)}
        className="min-h-11 max-w-none justify-start"
      />

      {error ? (
        <p id={errorId} className="flex items-start gap-1.5 text-body-sm text-on-red">
          <Icon name="bolt" size="xs" className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-body-sm text-ink-dim">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

const DEFAULT_TIERS = new Set(['site', 'line', 'asset'])
